import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Printer, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { fetchAllRecords, prefillFromRecord } from "@/lib/invoice";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import SkipOverride from "@/components/invoices/SkipOverride";
import SkipList from "@/components/invoices/SkipList";

export default function BatchInvoiceDialog({ open, onClose, db, table, base }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sent, setSent] = useState([]);

  const close = () => { setResult(null); onClose(); };

  const run = async () => {
    setBusy(true);
    try {
      const [records, settings] = await Promise.all([fetchAllRecords(api, base), api.get("/invoice-settings").then((r) => r.data)]);
      const invoices = records.map((r) => prefillFromRecord(r, table.columns, db.id, table.name, settings));
      const { data } = await api.post("/invoices/batch", { invoices });
      setSent(invoices);
      setResult(data);
      toast.success(`Created ${data.created.length} invoice${data.created.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg bg-card" data-testid="batch-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Invoice every record</DialogTitle>
          <DialogDescription>
            Creates one invoice for each of the {table.row_count} records in {table.name}, filled in from its fields.
            Records whose invoice number already exists are skipped; you can override skips afterwards.
          </DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-3 text-sm" data-testid="batch-result">
            <p><span className="font-bold text-emerald-400" data-testid="batch-created-count">{result.created.length}</span> created
              {result.skipped.length > 0 && <>, <span className="font-bold text-amber-400" data-testid="batch-skipped-count">{result.skipped.length}</span> skipped</>}</p>
            <SkipList skipped={result.skipped} />
            <SkipOverride result={result} invoices={sent} onDone={setResult} />
          </div>
        )}
        <DialogFooter className="gap-2">
          {!result ? (
            <>
              <Button variant="outline" onClick={close} data-testid="batch-cancel-button">Cancel</Button>
              <Button onClick={run} disabled={busy} className="hover:bg-blue-600 hover:text-white" data-testid="batch-create-button">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />} Create {table.row_count} invoices
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" asChild><Link to="/invoices" data-testid="batch-view-invoices">View invoices</Link></Button>
              {result.created.length > 0 && (
                <Button asChild className="hover:bg-blue-600 hover:text-white">
                  <a href={`/invoices/batch/${result.batch_id}/print`} target="_blank" rel="noreferrer" data-testid="batch-print-button"><Printer size={14} /> Print all</a>
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
